import { Component, Inject, OnDestroy, OnInit } from '@angular/core';
import { FormControl, FormGroup } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { BehaviorSubject, Subject, takeUntil } from 'rxjs';
import {
  SelectionDropdownInfo,
  SelectionOption,
} from 'src/app/modules/campaign/modules/campaign-shared-module/types/selection';
import { GeoCodeInfo } from 'src/app/shared/models/campaign/campaign-settings';
import { ToggleSettings } from '../../../../system-shared/models/toggle-settings';

export type LinkedinPrivacyData = {
  geoCode: string;
  allowTeamAccess: boolean;
  canAllowTeamAccess: boolean;
  geoCodeList: GeoCodeInfo[];
};

@Component({
  selector: 'app-linkedin-account-privacy-modal',
  templateUrl: './linkedin-account-privacy-modal.component.html',
  styleUrls: [
    '../shared/edit-modal-styles.scss',
    './linkedin-account-privacy-modal.component.scss',
  ],
  standalone: false,
})
export class LinkedinAccountPrivacyModalComponent implements OnInit, OnDestroy {
  constructor(
    public dialogRef: MatDialogRef<LinkedinAccountPrivacyModalComponent>,
    @Inject(MAT_DIALOG_DATA) public data?: LinkedinPrivacyData,
  ) {}

  private initialStringifiedValue: string = '';
  private $unsubscribe = new Subject<void>();

  saveDisabled$ = new BehaviorSubject<boolean>(true);

  countries: SelectionOption[] = [];
  teamAccessSettings: ToggleSettings | null = null;

  formGroup = new FormGroup({
    geoCode: new FormControl<string>(null),
    allowTeamAccess: new FormControl<boolean>(false),
  });

  selectionInfo: SelectionDropdownInfo = {
    noOptionsSearchText: 'CAMPAIGN.ACCOUNTS.LINKEDIN_ACCOUNTS.ADD_MODAL.NO_OPTIONS_COUNTRIES',
    maxWidthForSearch: 510,
  };

  closeModal(): void {
    this.dialogRef.close(null);
  }

  save(): void {
    const settings = this.formGroup.getRawValue();
    this.dialogRef.close(settings);
  }

  ngOnInit(): void {
    this.countries = this.data.geoCodeList.map((info: GeoCodeInfo) => {
      return {
        value: info.code,
        label: info.name,
      };
    });

    this.formGroup.patchValue({
      geoCode: this.data.geoCode,
      allowTeamAccess: this.data.allowTeamAccess,
    });

    this.teamAccessSettings = {
      label: 'CAMPAIGN.ACCOUNTS.LINKEDIN_ACCOUNTS.PRIVACY_MODAL.ALLOW_TEAM_ACCESS.LABEL',
      description: 'CAMPAIGN.ACCOUNTS.LINKEDIN_ACCOUNTS.PRIVACY_MODAL.ALLOW_TEAM_ACCESS.TOOLTIP',
      descriptionWidth: '306',
      isChecked: !!this.formGroup.get('allowTeamAccess').value,
      key: 'allowTeamAccess',
      disabled: !this.data.canAllowTeamAccess,
    };

    this.handleSaveButtonState();
  }

  onTeamAccessToggle(): void {
    this.teamAccessSettings.isChecked = !this.teamAccessSettings.isChecked;
    this.formGroup
      .get('allowTeamAccess')
      ?.patchValue(this.teamAccessSettings.isChecked, { emitEvent: true });
  }

  private handleSaveButtonState(): void {
    this.initialStringifiedValue = JSON.stringify(this.formGroup.getRawValue());

    this.formGroup.valueChanges.pipe(takeUntil(this.$unsubscribe)).subscribe((formValue) => {
      const saveDisabled =
        JSON.stringify(this.formGroup.getRawValue()) === this.initialStringifiedValue ||
        !formValue.geoCode;

      this.saveDisabled$.next(saveDisabled);
    });
  }

  ngOnDestroy(): void {
    this.$unsubscribe.next();
    this.$unsubscribe.complete();
  }
}
